import React, { useCallback, useState } from 'react';
import { Button, Group, Text, Badge, Box } from '@mantine/core';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import { formatDate, formatDateTime } from '../../utils/DateUtil.ts';
import { ParkingBilling } from '../../models/parkingBilling.ts';
import useDownloadBillingReport from '../../hooks/useDownloadBillingReport.tsx';
import useVoidBilling from '../../hooks/useVoidBilling.tsx';
import { notifications } from '@mantine/notifications';
import sortBy from 'lodash.sortby';
import useCreateRemittanceInstruction from '../../hooks/useCreateRemittanceInstruction.ts';
import { CreateRemittanceInstructionRequest } from '../../api/requests/create-remittance-instruction-request.ts';
import { Link } from 'react-router-dom';
import { closeAllModals, modals } from '@mantine/modals';

type ConfirmedBillingTableProps = {
  data: ParkingBilling[];
  isLoading: boolean;
};

export default function ConfirmedBillingTable({
  data = [],
  isLoading,
}: ConfirmedBillingTableProps) {
  const [selectedRecords, setSelectedRecords] = useState([]);
  const { mutate: downloadBillReport } = useDownloadBillingReport();
  const { mutate: voidBilling } = useVoidBilling();
  const { mutate: createRemittanceInstruction } =
    useCreateRemittanceInstruction();

  const records = sortBy(data, 'readyDateTime').reverse();

  const handleGenerate = (row: ParkingBilling) => {
    const modalId = modals.openContextModal({
      modal: 'confirmModal',
      title: `產生匯款單`,
      innerProps: {
        modalBody: `確定要產生匯款單 ${row.name} 嗎？`,
        loading: false,
        onConfirm: () => {
          modals.updateContextModal({
            modalId,
            innerProps: {
              loading: true,
            },
          });
          const request: CreateRemittanceInstructionRequest = {
            parkingBillingIds: [row.id],
          };
          createRemittanceInstruction(request, {
            onSuccess: () => {
              notifications.show({
                title: '匯款單產生成功',
                message: `對帳單 ${row.name} 的匯款單已成功產生`,
                color: 'green',
              });
              closeAllModals();
            },
            onSettled: () => {
              modals.updateContextModal({
                modalId,
                innerProps: {
                  loading: false,
                },
              });
            },
          });
        },
      },
    });
  };

  const handleDownload = (row: ParkingBilling) => {
    downloadBillReport(row.id, {
      onSuccess: () => {
        notifications.show({
          title: '下載成功',
          message: `對帳單 ${row.name} 的報表已下載`,
          color: 'green',
        });
      },
    });
  };

  const handleVoid = (row: ParkingBilling) => {
    const modalId = modals.openContextModal({
      modal: 'confirmModal',
      title: `作廢對帳單`,
      innerProps: {
        modalBody: `確定要作廢對帳單 ${row.name} 嗎？`,
        loading: false,
        onConfirm: () => {
          modals.updateContextModal({
            modalId,
            innerProps: {
              loading: true,
            },
          });
          voidBilling(row.id, {
            onSuccess: () => {
              notifications.show({
                title: '成功',
                message: `對帳單 ${row.name} 已作廢`,
                color: 'green',
              });
              closeAllModals();
            },
            onSettled: () => {
              modals.updateContextModal({
                modalId,
                innerProps: {
                  loading: false,
                },
              });
            },
          });
        },
      },
    });
  };

  const handleBatch = useCallback(() => {
    const modalId = modals.openContextModal({
      modal: 'confirmModal',
      title: `批次產生匯款單，共 ${selectedRecords.length} 筆`,
      innerProps: {
        modalBody: `確定要批次產生匯款單嗎？`,
        loading: false,
        onConfirm: () => {
          modals.updateContextModal({
            modalId,
            innerProps: {
              loading: true,
            },
          });
          const ids = selectedRecords.map((x) => x.id);
          const request: CreateRemittanceInstructionRequest = {
            parkingBillingIds: ids,
          };
          createRemittanceInstruction(request, {
            onSuccess: () => {
              notifications.show({
                title: '匯款單批次產生成功',
                message: `已成功為 ${ids.length} 筆對帳單產生匯款單`,
                color: 'green',
              });
              setSelectedRecords([]);
              closeAllModals();
            },
            onSettled: () => {
              modals.updateContextModal({
                modalId,
                innerProps: {
                  loading: false,
                },
              });
            },
          });
        },
      },
    });
  }, [selectedRecords, createRemittanceInstruction]);

  const columns: DataTableColumn<ParkingBilling>[] = [
    {
      title: '對帳單/廠商',
      accessor: 'name',
      render: (row) => (
        <div>
          <Text size="sm">{row.name}</Text>
          <Text size="sm" c="dimmed">
            {row.siteProvider.name}
          </Text>
        </div>
      ),
    },
    {
      title: '對帳期間',
      accessor: 'period',
      render: ({ billDateTimeFrom, billDateTimeTo }) => {
        return `${formatDate(billDateTimeFrom)} ~ ${formatDate(billDateTimeTo)}`;
      },
    },
    {
      title: '匯款金額',
      accessor: 'amount',
      render: ({ summary }) => summary.totalRemittanceAmount.toLocaleString(),
    },
    {
      title: '手續費',
      accessor: 'fee',
      render: ({ summary }) => summary.totalFeeAmount.toLocaleString(),
    },
    {
      title: '廠商開立發票金額',
      accessor: 'inputInvoiceAmount',
      render: ({ summary }) => {
        return summary.inputInvoiceAmount.toLocaleString();
      },
    },
    {
      title: '標籤',
      accessor: 'tags',
      render: (row) => (
        <Group>
          {row.tags?.map((tag) => (
            <Badge key={tag.id} variant="outline">
              {tag.name}
            </Badge>
          ))}
        </Group>
      ),
    },
    {
      title: '產檔時間',
      accessor: 'generatedAt',
      render: ({ readyDateTime }) => {
        return formatDateTime(readyDateTime);
      },
    },
    {
      title: '操作',
      accessor: 'actions',
      render: (row) => (
        <Group>
          {row.readyRemittanceInstructionId !== null && (
            <Button
              component={Link}
              to={`/remittance-instructions/${row.readyRemittanceInstructionId}`}
              variant="light"
              size="xs"
            >
              前往匯款單
            </Button>
          )}
          {row.readyRemittanceInstructionId === null && (
            <Button size="xs" onClick={() => handleGenerate(row)}>
              產生匯款單
            </Button>
          )}
          <Button
            size="xs"
            variant="outline"
            onClick={() => handleDownload(row)}
          >
            下載報表
          </Button>
          <Button
            size="xs"
            color="red"
            variant="outline"
            onClick={() => handleVoid(row)}
          >
            作廢
          </Button>
        </Group>
      ),
    },
  ];

  return (
    <Box>
      {selectedRecords.length > 0 && (
        <Group mt="md">
          <Text>已勾選 {selectedRecords.length} 筆對帳單</Text>
          <Button onClick={() => handleBatch()}>批次產生匯款單</Button>
        </Group>
      )}
      <DataTable
        idAccessor="id"
        highlightOnHover
        minHeight={150}
        columns={columns}
        records={records}
        selectedRecords={selectedRecords}
        onSelectedRecordsChange={setSelectedRecords}
        isRecordSelectable={(row) => row.readyRemittanceInstructionId === null}
        verticalSpacing="xs"
        fetching={isLoading}
        noRecordsText="沒有已確認對帳單"
      />
    </Box>
  );
}
